import { verifyOtp } from './api'
import { t } from './translations'

/**
 * OwnID "Boost" login-button flow (used by web-payments), distinct from the
 * `start()` drawer flow (ap-sync-ui). `ownid('login', …)` injects a standalone
 * button into `targetField`; clicking it opens a Windows-style modal with the
 * OTP form, and on success calls `onLogin({ jwt, metadata: { loginId } })`.
 *
 * Structure + styling mirror the real SDK (captured from the live widget):
 *   - <ownid-standalone-button-widget> (own shadow), themed via --ownid-button-widget-*
 *   - .modal-win shell + .boostLoginIdVerification content
 */

const OTP_LENGTH = 4

/** How long the in-button spinner shows on click before the modal opens. */
const BUTTON_SPINNER_DELAY_MS = 800

export interface LoginOptions {
  variant?: string
  targetField?: HTMLElement | null
  loginIdField?: HTMLElement | null
  loginIdPrefixField?: HTMLElement | null
  tooltip?: unknown
  addOrStatement?: boolean
  onLogin?: (data: {
    jwt: string
    metadata: { loginId: string }
  }) => void | Promise<void>
  onError?: (error: unknown) => void | Promise<void>
}

/**
 * OwnID's "adaptive logo" — on Windows it's the 4-square logo (the real button's
 * aria-label is "Biometric Authentication"). Rendered in currentColor (white on
 * the blue button). Platform-static here; the real SDK swaps it per OS.
 */
const ADAPTIVE_LOGO_ICON =
  '<svg viewBox="0 0 42 42" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.465 2.465h17.652v17.652H2.465V2.465zm19.418 0h17.652v17.652H21.883V2.465zM2.465 21.883h17.652v17.652H2.465V21.883zm19.418 0h17.652v17.652H21.883V21.883z"/></svg>'

/** Close "×" icon (exact data-URI captured from OwnID's modal-win close button). */
const CLOSE_ICON =
  "url(\"data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMyIgaGVpZ2h0PSIxMyI+PHBhdGggZD0iTTEuNSAxLjVsMTAgMTBtLTEwIDBsMTAtMTAiIHN0cm9rZT0iIzVmNjQ2NyIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPgo=\")"

const SPINNER =
  '<div class="ownid-spinner">' +
  '<svg viewBox="0 0 100 100" aria-hidden="true"><circle class="ownid-spinner-bg" r="42.5" cx="50" cy="50"></circle></svg>' +
  '<svg class="ownid-spinner-arc-svg" viewBox="0 0 100 100" aria-hidden="true"><circle class="ownid-spinner-arc" r="42.5" cx="50" cy="50"></circle></svg>' +
  '</div>'

const BUTTON_STYLES = `
:host {
  display: block;
  /* host font-family so the button (which uses the var without a fallback,
     like the real SDK) inherits something sane when the app doesn't set it. */
  font-family: var(--ownid-button-widget-font-family, 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif);
}
* { box-sizing: border-box; }
/* Wrapper structure mirrors OwnID (wrapper → popover container → button) so the
 * button reliably fills its full width and centers its content. */
.ownid-wrapper {
  display: flex;
  align-items: center;
  gap: var(--ownid-button-widget-or-gap, 0);
}
.ownid-popover-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
/* Exact properties from OwnID's .button-module__button. */
.ownid-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: var(--ownid-button-width, 100%);
  height: var(--ownid-button-height, 50px);
  margin: var(--ownid-button-widget-button-margin);
  padding: var(--ownid-button-widget-button-padding, 0 10px);
  overflow: hidden;
  white-space: nowrap;
  font-size: var(--ownid-button-widget-font-size);
  font-family: var(--ownid-button-widget-font-family);
  color: var(--ownid-button-widget-color, #0070f2);
  background: var(--ownid-button-widget-background, #ffffff);
  border: var(--ownid-button-widget-border-width) solid var(--ownid-button-widget-border-color, #d0d0d0);
  border-radius: var(--ownid-button-widget-border-radius);
  box-shadow: var(--ownid-button-widget-box-shadow);
  cursor: pointer;
  transition: box-shadow 0.2s;
  /* Spinner palette — exactly as OwnID sets it on .button-module__button. */
  --ownid-spinner-overlay-bg-color: var(--ownid-button-widget-background, #ffffff);
  --ownid-spinner-color: var(--ownid-button-widget-color, #0070f2);
  --ownid-spinner-bg-color: var(--ownid-button-widget-color, #0070f2);
  --ownid-spinner-bg-opacity: 0.3;
}
.ownid-btn__icon { display: flex; flex: none; }
.ownid-btn__icon svg { width: 25.5px; height: 25.5px; display: block; fill: currentColor; }
/* .text-module__text — preserve the newline in the label so it wraps like OwnID. */
.ownid-btn__label { white-space: pre-line; text-align: center; }

/* In-button loading spinner: overlay covers icon/label, spinner spins centered. */
.ownid-btn__spinner {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  background: var(--ownid-spinner-overlay-bg-color, var(--ownid-button-widget-background, #3e68dd));
}
.ownid-btn.is-loading .ownid-btn__spinner { display: flex; }
.ownid-spinner { position: relative; width: 24px; height: 24px; }
.ownid-spinner svg { position: absolute; inset: 0; width: 24px; height: 24px; }
.ownid-spinner circle { fill: none; stroke-width: 15; }
.ownid-spinner-bg {
  stroke: var(--ownid-spinner-bg-color, #fff);
  opacity: var(--ownid-spinner-bg-opacity, 0.3);
}
.ownid-spinner-arc {
  stroke: var(--ownid-spinner-color, #fff);
  stroke-linecap: round;
  animation: ownid-btn-dash 2s cubic-bezier(0.61, 0.24, 0.44, 0.79) infinite;
}
.ownid-spinner-arc-svg {
  transform-origin: center;
  animation: ownid-btn-rotate 2s cubic-bezier(0.61, 0.24, 0.44, 0.79) infinite;
}
@keyframes ownid-btn-rotate { 100% { transform: rotate(720deg); } }
@keyframes ownid-btn-dash {
  0% { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
  50% { stroke-dasharray: 80 270; stroke-dashoffset: 220; }
  100% { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
}
`

const MODAL_STYLES = `
:host { all: initial; }
* { box-sizing: border-box; }
.ownid-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483645;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 16px;
  background: hsla(0, 0%, 80%, 0.69);
  font-family: Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.ownid-modal {
  position: relative;
  width: 400px;
  max-width: 100%;
  padding: 24px;
  background: #fff;
  border: 0.8px solid #e6e6e6;
  border-radius: 9px;
  box-shadow: rgba(0, 0, 0, 0.25) 0 6px 16px 0;
  color: #202124;
  animation: ownid-modal-top 0.4s;
}
@keyframes ownid-modal-top { from { top: -300px; opacity: 0; } to { top: 0; opacity: 1; } }
.ownid-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 21px;
  height: 21px;
  padding: 4px;
  border: none;
  font-size: 0;
  cursor: pointer;
  background: transparent ${CLOSE_ICON} no-repeat center / 13px 13px;
}
.ownid-modal__header {
  margin: 0 0 24px;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: #202124;
}
.ownid-modal__description {
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: #60646a;
  text-align: center;
  white-space: pre-line;
}
.ownid-modal__title {
  margin: 18px 0 16px;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  color: #292929;
  text-align: center;
}
.otp-input-module__form { display: flex; justify-content: center; }
.otp-input-module__input { margin: 0 4px; }
.otp-input-module__input input {
  width: 52px;
  height: 52px;
  padding: 0;
  text-align: center;
  font-family: Arial, sans-serif;
  font-size: 32px;
  color: #1c1b1f;
  background: #fff;
  border: 0.8px solid #5f6467;
  border-radius: 2px;
  outline: none;
  -moz-appearance: textfield;
}
.otp-input-module__input input::-webkit-outer-spin-button,
.otp-input-module__input input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.otp-input-module__input input:focus { border-color: #2f7cf6; }
.ownid-modal.is-error .otp-input-module__input input { border-color: #ff3b30; }
.ownid-modal__error {
  min-height: 18px;
  margin: 7px 0 13px;
  font-size: 13px;
  line-height: 18px;
  color: #ff3b30;
  text-align: center;
}
.ownid-modal__resend {
  font-size: 14px;
  line-height: 24px;
  color: #5f6467;
  text-align: center;
}
.ownid-modal__resend-cta {
  color: #2f7cf6;
  cursor: pointer;
  margin-left: 6px;
}
.ownid-modal__loader { display: flex; justify-content: center; height: 30px; }
.ownid-spinner { position: relative; width: 30px; height: 30px; }
.ownid-spinner svg { position: absolute; inset: 0; width: 30px; height: 30px; }
.ownid-spinner circle { fill: none; stroke-width: 8; }
.ownid-spinner-bg { stroke: #e6e8ec; }
.ownid-spinner-arc { stroke: #2f7cf6; stroke-linecap: round; animation: ownid-dash 2s cubic-bezier(0.61,0.24,0.44,0.79) infinite; }
.ownid-spinner-arc-svg { transform-origin: center; animation: ownid-rotate 2s cubic-bezier(0.61,0.24,0.44,0.79) infinite; }
@keyframes ownid-rotate { 100% { transform: rotate(720deg); } }
@keyframes ownid-dash {
  0% { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
  50% { stroke-dasharray: 80 270; stroke-dashoffset: 220; }
  100% { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
}
`

/** Render the standalone login button into the target field and wire its click. */
export function mountLoginButton(opts: LoginOptions, apiUrl: string): void {
  const target = opts.targetField
  if (!target) {
    console.error('[ownid-mock] login: targetField not found')
    return
  }
  target.querySelectorAll('ownid-standalone-button-widget').forEach((e) => e.remove())

  const widget = document.createElement('ownid-standalone-button-widget')
  widget.setAttribute('data-ownid-widget-id', 'login')
  // Safety net for hosts that hide undefined/!defined custom elements.
  widget.style.setProperty('visibility', 'visible', 'important')
  const shadow = widget.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = BUTTON_STYLES
  shadow.appendChild(style)

  const btn = document.createElement('button')
  btn.className = 'ownid-btn'
  btn.type = 'button'
  btn.setAttribute('aria-label', 'Biometric Authentication')
  const icon = document.createElement('span')
  icon.className = 'ownid-btn__icon'
  icon.innerHTML = ADAPTIVE_LOGO_ICON
  const label = document.createElement('span')
  label.className = 'ownid-btn__label'
  label.textContent = t('boost.cta')
  const spinner = document.createElement('div')
  spinner.className = 'ownid-btn__spinner'
  spinner.innerHTML = SPINNER
  btn.append(icon, label, spinner)

  // wrapper → popover container → button (mirrors the real SDK DOM)
  const wrapper = document.createElement('div')
  wrapper.className = 'ownid-wrapper'
  wrapper.dir = 'ltr'
  const container = document.createElement('div')
  container.className = 'ownid-popover-container'
  container.appendChild(btn)
  wrapper.appendChild(container)
  shadow.appendChild(wrapper)
  target.appendChild(widget)

  // Click → in-button spinner (like OwnID "sending the code"); it keeps running
  // while the modal is open and stops only when the modal is dismissed (cancel
  // or a terminal error). On success it stays until the app navigates away.
  btn.addEventListener('click', () => {
    if (btn.classList.contains('is-loading')) return
    // Like the real SDK: do nothing if no login id (phone) has been entered.
    if (!fieldValue(opts.loginIdField).replace(/\D/g, '')) {
      console.info('[ownid-mock] login: no phone number entered — ignoring click')
      return
    }
    btn.classList.add('is-loading')
    const stopSpinner = () => btn.classList.remove('is-loading')
    setTimeout(() => new LoginModal(opts, apiUrl, stopSpinner).open(), BUTTON_SPINNER_DELAY_MS)
  })
}

class LoginModal {
  private host!: HTMLElement
  private modal!: HTMLDivElement
  private inputs: HTMLInputElement[] = []
  private errorEl!: HTMLDivElement
  private resendRow!: HTMLDivElement
  private loaderRow!: HTMLDivElement
  private submitting = false
  private readonly phone: string

  constructor(
    private readonly opts: LoginOptions,
    private readonly apiUrl: string,
    private readonly onDismiss: () => void
  ) {
    this.phone = resolvePhone(opts)
  }

  open(): void {
    this.host = document.createElement('ownid-root')
    this.host.setAttribute('data-ownid-mock-modal', '')
    this.host.style.setProperty('visibility', 'visible', 'important')
    const shadow = this.host.attachShadow({ mode: 'open' })
    const style = document.createElement('style')
    style.textContent = MODAL_STYLES
    shadow.appendChild(style)

    const overlay = document.createElement('div')
    overlay.className = 'ownid-overlay'

    this.modal = document.createElement('div')
    this.modal.className = 'ownid-modal'
    this.modal.setAttribute('role', 'dialog')
    this.modal.tabIndex = -1

    const close = document.createElement('button')
    close.className = 'ownid-modal__close'
    close.type = 'button'
    close.setAttribute('aria-label', 'Cancel')
    close.addEventListener('click', () => this.dismiss())

    const header = el('div', 'ownid-modal__header', t('boost.header'))
    const description = el(
      'div',
      'ownid-modal__description',
      t('boost.description', { loginId: this.phone })
    )
    const title = el('div', 'ownid-modal__title', t('boost.title'))

    const form = document.createElement('div')
    form.className = 'otp-input-module__form'
    for (let i = 0; i < OTP_LENGTH; i++) {
      const box = document.createElement('span')
      box.className = 'otp-input-module__input'
      const input = this.buildDigitInput(i)
      box.appendChild(input)
      form.appendChild(box)
      this.inputs.push(input)
    }

    this.errorEl = document.createElement('div')
    this.errorEl.className = 'ownid-modal__error'
    this.errorEl.setAttribute('aria-live', 'assertive')

    this.loaderRow = document.createElement('div')
    this.loaderRow.className = 'ownid-modal__loader'
    this.loaderRow.style.display = 'none'
    this.loaderRow.innerHTML = SPINNER

    this.resendRow = document.createElement('div')
    this.resendRow.className = 'ownid-modal__resend'
    const resendQ = document.createElement('span')
    resendQ.textContent = t('boost.resendQuestion')
    const resendCta = document.createElement('span')
    resendCta.className = 'ownid-modal__resend-cta'
    resendCta.setAttribute('role', 'button')
    resendCta.tabIndex = 0
    resendCta.textContent = t('boost.resend')
    resendCta.addEventListener('click', () => this.reset(true))
    this.resendRow.append(resendQ, resendCta)

    const content = document.createElement('div')
    content.setAttribute('data-ownid-content', '')
    content.append(header, description, title, form, this.errorEl, this.loaderRow, this.resendRow)

    this.modal.append(close, content)
    overlay.appendChild(this.modal)
    shadow.appendChild(overlay)
    document.body.appendChild(this.host)

    this.inputs[0]?.focus()
  }

  private buildDigitInput(index: number): HTMLInputElement {
    const input = document.createElement('input')
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'one-time-code'
    input.maxLength = 1
    input.setAttribute('data-testid', `ownid-otp-input-${index}`)
    input.setAttribute('aria-label', `OTP digit ${index + 1} of ${OTP_LENGTH}`)

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 1)
      if (input.value && index < OTP_LENGTH - 1) this.inputs[index + 1].focus()
      this.onChange()
    })
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !input.value && index > 0) {
        this.inputs[index - 1].focus()
      }
    })
    input.addEventListener('paste', (e) => {
      e.preventDefault()
      const text = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, OTP_LENGTH)
      if (!text) return
      ;[...text].forEach((c, i) => {
        if (this.inputs[i]) this.inputs[i].value = c
      })
      this.inputs[Math.min(text.length, OTP_LENGTH - 1)].focus()
      this.onChange()
    })
    return input
  }

  private get code(): string {
    return this.inputs.map((i) => i.value).join('')
  }

  private onChange(): void {
    this.clearError()
    if (this.code.length === OTP_LENGTH && !this.submitting) this.submit()
  }

  private async submit(): Promise<void> {
    if (this.submitting || this.code.length !== OTP_LENGTH) return
    this.setSubmitting(true)

    const result = await verifyOtp({
      apiUrl: this.apiUrl,
      code: this.code,
      phoneNumber: this.phone,
    })

    if (result.ok) {
      this.teardown()
      await safe(() =>
        this.opts.onLogin?.({ jwt: result.jwt, metadata: { loginId: this.phone } })
      )
      return
    }

    // Terminal failures end the flow via onError; OTP-entry errors stay inline.
    if (
      result.code === 'ACCOUNT_NOT_FOUND' ||
      result.code === 'NETWORK' ||
      result.code === 'BAD_RESPONSE'
    ) {
      this.dismiss()
      await safe(() => this.opts.onError?.(makeError(result.code, result.message)))
      return
    }

    this.showError(errorMessageForCode(result.code, result.message))
    this.setSubmitting(false)
    this.reset(true)
  }

  private setSubmitting(value: boolean): void {
    this.submitting = value
    this.inputs.forEach((i) => (i.disabled = value))
    this.loaderRow.style.display = value ? 'flex' : 'none'
    this.resendRow.style.display = value ? 'none' : 'block'
  }

  private reset(focus: boolean): void {
    this.inputs.forEach((i) => {
      i.value = ''
      i.disabled = false
    })
    if (focus) this.inputs[0]?.focus()
  }

  private showError(message: string): void {
    this.errorEl.textContent = message
    this.modal.classList.add('is-error')
  }

  private clearError(): void {
    if (!this.errorEl.textContent) return
    this.errorEl.textContent = ''
    this.modal.classList.remove('is-error')
  }

  private teardown(): void {
    this.host.remove()
  }

  /** Close the modal AND stop the button spinner (cancel / terminal error). */
  private dismiss(): void {
    this.teardown()
    this.onDismiss()
  }
}

function resolvePhone(opts: LoginOptions): string {
  const prefix = fieldValue(opts.loginIdPrefixField).trim()
  const num = fieldValue(opts.loginIdField).trim()
  const raw = num.startsWith('+') ? num : prefix + num
  // Keep only digits, with a single leading '+' (strip spaces/parens/dashes).
  const digits = raw.replace(/\D/g, '')
  return (raw.trim().startsWith('+') ? '+' : '') + digits
}

/** Read `.value`, tolerating an id placed on a wrapper around the real <input>. */
function fieldValue(field?: HTMLElement | null): string {
  if (!field) return ''
  if (field instanceof HTMLInputElement) return field.value
  const input = field.querySelector?.('input')
  return input?.value ?? ''
}

function el(tag: string, className: string, text: string): HTMLElement {
  const node = document.createElement(tag)
  node.className = className
  node.textContent = text
  return node
}

function errorMessageForCode(code: string, serverMessage?: string): string {
  const key = `otp.errors.${code}`
  const translated = t(key)
  if (translated !== key) return translated
  if (serverMessage) return serverMessage
  return t('otp.errors.default')
}

/**
 * Build an Error for onError — web-payments' handleOnError runs it through
 * ErrorManager.logError and reads `.name` / `.message`, so pass a real Error
 * with the BE code as the name and a human-readable message.
 */
function makeError(code: string, serverMessage?: string): Error {
  const err = new Error(serverMessage || errorMessageForCode(code))
  err.name = code
  return err
}

async function safe(fn?: () => void | Promise<void>): Promise<void> {
  if (!fn) return
  try {
    await fn()
  } catch (err) {
    console.error('[ownid-mock] login handler threw', err)
  }
}
